/**
 * Client-side profile API module — replaces profile.action.ts Server Actions.
 */

import { PROFILE } from "@/shared/constants/backend-api"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { Profile } from "@/features/auth/types/auth-actions.type"
import { getNonHomeownerUserById } from "@/features/volunteers/api/volunteers.api"
import { mapNonHomeownerUserToProfile } from "../lib/map-volunteer-to-profile"
import { UpdateAboutMeForm } from "../schemas/profile-form.schema"
import { ProfileLocationForm } from "../schemas/profile-location-form.schema"
import { StmInformationFormSchema } from "../schemas/stm-information-form.schema"
import { ProfileBaseInformationFormType } from "../schemas/profile-base-information-form.schema"
import { objectToFormData } from "@/shared/lib/utils"

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const PROFILE_QUERY_KEYS = {
  all: ["profile"] as const,
  detail: (userId: number) => ["profile", userId] as const,
  images: (userId: number) => ["profile", "images", userId] as const,
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getProfile(userId: number): Promise<ApiResponse<Profile>> {
  return authFetchJson<Profile>(PROFILE.base(userId))
}

export async function getVolunteerProfile(
  userId: number,
): Promise<{ success: true; data: Profile } | { success: false; message: string }> {
  const res = await getNonHomeownerUserById(userId)
  if (!res.success || !res.data) {
    return { success: false, message: !res.success ? res.message : "Failed to load profile" }
  }
  const profile = mapNonHomeownerUserToProfile(res.data)
  return { success: true, data: profile }
}

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export async function updateProfile(
  userId: number,
  data: UpdateAboutMeForm,
): Promise<ApiResponse<UpdateAboutMeForm>> {
  return authFetchJson<UpdateAboutMeForm>(PROFILE.base(userId), {
    method: "PUT",
    body: JSON.stringify(data),
  })
}

export async function createProfileLocation(
  userId: number,
  data: ProfileLocationForm,
): Promise<ApiResponse<Location>> {
  const formData = objectToFormData(data)
  return authFetchJson<Location>(PROFILE.location(userId), {
    method: "POST",
    body: formData,
  })
}

export async function createProfileStm(
  userId: number,
  data: StmInformationFormSchema,
): Promise<ApiResponse<StmInformationFormSchema>> {
  return authFetchJson<StmInformationFormSchema>(PROFILE.stm(userId), {
    method: "PUT",
    body: JSON.stringify(data),
  })
}

export async function createProfileBaseInformation(
  userId: number,
  data: ProfileBaseInformationFormType,
): Promise<ApiResponse<ProfileBaseInformationFormType>> {
  const formData = objectToFormData(data)
  return authFetchJson<ProfileBaseInformationFormType>(PROFILE.baseProfile(userId), {
    method: "POST",
    body: formData,
  })
}

export async function uploadProfilePicture(
  userId: number,
  file: File,
): Promise<ApiResponse<any>> {
  const formData = new FormData()
  formData.append("profilePicture", file)
  return authFetchJson<any>(`${PROFILE.base(userId)}/profile-picture`, {
    method: "POST",
    body: formData,
  })
}

export async function updateSelectedRegion(
  userId: number,
  selectedRegionId: string | number,
): Promise<ApiResponse<Profile>> {
  const regionIdNumber = typeof selectedRegionId === "string"
    ? parseInt(selectedRegionId, 10)
    : selectedRegionId
  if (isNaN(regionIdNumber)) throw new Error("Invalid region ID")

  return authFetchJson<Profile>(PROFILE.base(userId), {
    method: "PUT",
    body: JSON.stringify({ selectedRegion: regionIdNumber }),
  })
}

export async function updateProfileWithGoogleSignupInfo(
  userId: number,
  data: { fullname?: string; phone?: string },
): Promise<{ success: boolean; message?: string }> {
  const updateData: Record<string, string> = {}
  if (data.fullname?.trim()) updateData.fullname = data.fullname.trim()
  if (data.phone?.trim()) updateData.phone = data.phone.trim()

  if (!updateData.fullname && !updateData.phone) {
    return { success: false, message: "No valid data to update" }
  }

  const res = await authFetchJson<Profile>(PROFILE.base(userId), {
    method: "PUT",
    body: JSON.stringify(updateData),
  })
  return res.success ? { success: true } : { success: false, message: res.message }
}

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export async function deleteProfilePicture(userId: number): Promise<ApiResponse<{ profilePicture: null }>> {
  return authFetchJson<{ profilePicture: null }>(`${PROFILE.base(userId)}/profile-picture`, {
    method: "DELETE",
  })
}

export async function deleteLocationImage(
  userId: number,
  imageUrl: string,
): Promise<ApiResponse<{ locationImages: string[] }>> {
  const encodedImageUrl = encodeURIComponent(imageUrl)
  return authFetchJson<{ locationImages: string[] }>(`${PROFILE.location(userId)}/${encodedImageUrl}`, {
    method: "DELETE",
  })
}

export async function deleteBaseImage(
  userId: number,
  imageUrl: string,
): Promise<ApiResponse<{ baseImages: string[] }>> {
  const encodedImageUrl = encodeURIComponent(imageUrl)
  return authFetchJson<{ baseImages: string[] }>(`${PROFILE.baseProfile(userId)}/${encodedImageUrl}`, {
    method: "DELETE",
  })
}
